// components/ClientCategoryTemplateSelector.tsx
import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    Typography,
} from "@mui/material";

// API
import useAuthToken from '../../functions/useAuthToken';
import { useUser } from '../../components/UserContext';
import { useApiClient } from '../../components/useApiClient';

// Category template
import { CategoryTemplate, TemplateCategory, TemplateCategoryPreviewGroup, CategoryTemplateSummary } from '../../models/Category';

interface Props {
    onContinue: (templateId: number | null) => void;
}

const ClientCategoryTemplateSelector: React.FC<Props> = ({ onContinue }) => {
    const [templates, setTemplates] = useState<CategoryTemplateSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

    // For "View full categories"
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [previewTemplate, setPreviewTemplate] = useState<CategoryTemplateSummary | null>(
        null
    );
    const [previewGroups, setPreviewGroups] = useState<TemplateCategoryPreviewGroup[]>([]);

    // API Use
    const { user, activeClientId, invalidateUser } = useUser();
    const { get, put, post } = useApiClient();
    const getAccessToken = useAuthToken();

    // Load templates
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const accessToken = await getAccessToken();
                if (!accessToken) return;

                const res = await get<CategoryTemplateSummary[]>(`categories/templates`, accessToken);

                setTemplates(res);

                // Optionally auto-select default template
                const defaultTemplate = res.find((t) => t.isDefault);
                if (defaultTemplate) {
                    setSelectedTemplateId(defaultTemplate.categoryTemplateId);
                    handleContinue(defaultTemplate.categoryTemplateId);
                }
            } catch (e: any) {
                setError(e?.response?.data ?? e?.message ?? "Failed to load templates.");
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, []);

    const handleViewDetails = async (template: CategoryTemplateSummary) => {
        setPreviewTemplate(template);
        setPreviewOpen(true);
        setPreviewGroups([]);
        setPreviewError(null);
        setPreviewLoading(true);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const res = await get<TemplateCategoryPreviewGroup[]>(`categories/template-preview/${template.categoryTemplateId}`, accessToken);

            setPreviewGroups(res);
        } catch (e: any) {
            setPreviewError(e?.response?.data ?? e?.message ?? "Failed to load categories.");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleContinue = (selectedId: number) => {
        onContinue(selectedId);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
                <Typography variant="h6" gutterBottom>
                    Choose how to set up initial categories
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Select the option that best describes this client/collection. Categories will be pre-populated
                    based on your choice. You can review and edit these under 'Manage Categories' after creation.
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "error.light",
                        bgcolor: "error.lighter",
                        borderRadius: 1,
                        p: 2,
                    }}
                >
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                </Box>
            )}

            {!loading && !error && templates.length > 0 && (
                <FormControl component="fieldset" fullWidth>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        {templates.map((t) => (
                            <Card
                                key={t.categoryTemplateId}
                                variant="outlined"
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderColor:
                                        selectedTemplateId === t.categoryTemplateId
                                            ? "primary.main"
                                            : "divider",
                                    boxShadow:
                                        selectedTemplateId === t.categoryTemplateId
                                            ? 3
                                            : 0,
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={selectedTemplateId === t.categoryTemplateId}
                                                onChange={() => { setSelectedTemplateId(t.categoryTemplateId); handleContinue(t.categoryTemplateId); }}
                                                value={t.categoryTemplateId}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {t.name}
                                            </Typography>
                                        }
                                    />

                                    {t.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 1 }}
                                        >
                                            {t.description}
                                        </Typography>
                                    )}

                                    {t.exampleCategories && t.exampleCategories.length > 0 && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", mb: 0.5 }}
                                            >
                                                Includes:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                                {t.exampleCategories.join(", ")}
                                                {t.exampleCategories.length >= 3 && "…"}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>

                                <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: "space-between" }}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleViewDetails(t)}
                                    >
                                        View details
                                    </Button>
                                    {/*<Button*/}
                                    {/*    size="small"*/}
                                    {/*    variant={*/}
                                    {/*        selectedTemplateId === t.categoryTemplateId*/}
                                    {/*            ? "contained"*/}
                                    {/*            : "outlined"*/}
                                    {/*    }*/}
                                    {/*    onClick={() => setSelectedTemplateId(t.categoryTemplateId)}*/}
                                    {/*>*/}
                                    {/*    Select*/}
                                    {/*</Button>*/}
                                </CardActions>
                            </Card>
                        ))}
                    </Box>
                </FormControl>
            )}

            {/* Details dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {previewTemplate
                        ? `Categories for ${previewTemplate.name}`
                        : "Template categories"}
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {previewError && (
                        <Typography variant="body2" color="error">
                            {previewError}
                        </Typography>
                    )}

                    {!previewLoading && !previewError && previewGroups.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No category preview available.
                        </Typography>
                    )}

                    {!previewLoading && !previewError && previewGroups.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {previewGroups.map((group, idx) => (
                                //<Box key={idx}>
                                //    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                //        {group.parentName}
                                //    </Typography>
                                //    <Typography variant="body2" sx={{ ml: 2, mt: 0.5 }}>
                                //        {group.childNames.join(", ")}
                                //    </Typography>
                                //</Box>
                                <div key={idx} className="border rounded-lg overflow-hidden">
                                <div className="bg-gray-50 px-4 py-3 font-medium text-gray-900">
                                        {group.parentName}
                                        {group.childNames.length != 0 && 
                                    <span className="ml-2 text-sm text-gray-500">
                                                ({group.childNames.length} {group.childNames.length === 1 ? "subcategory"  : "subcategories"})
                                        </span>
                                    }
                                </div>
                                <div className="px-4 py-3 bg-white">
                                    <div className="flex flex-wrap gap-2">
                                    {group.childNames.map((child, idx) => (
                                        <span 
                                        key={idx} 
                                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                                        >
                                        {child}
                                        </span>
                                    ))}
                                    </div>
                                </div>
                                </div>

                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ClientCategoryTemplateSelector;
